import { extractShortcode } from '@/utils/shortcode';
import { pickOwnerPerson, type AccountOwnerPerson } from '@/utils/work-order-owner-autofill';

/**
 * What picking an account writes into a single-line location field (the work-order job
 * location): the lot shortcode from the address, else the account number, else the address
 * itself with its lines joined by ", " — a single-line input silently drops the newline, which
 * ran "5609 Eventing Way\nHilliard, OH" together into "5609 Eventing WayHilliard, OH".
 */
export const accountLotFillValue = (a: { address?: string | null; accountNumber?: string | null }): string => {
	const shortcode = extractShortcode(a.address) ?? a.accountNumber;
	if (shortcode) return shortcode;
	return (a.address ?? '')
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter(Boolean)
		.join(', ');
};

export interface AccountLabelInput {
	accountNumber?: string | null;
	externalName?: string | null;
	primaryOwnerName?: string | null;
	/**
	 * Account-level company (#424). Note this ranks BELOW `primaryOwnerName`
	 * here, unlike in `accountDisplayName` where it ranks above the owner's
	 * company. Deliberate: this label answers "who do I pick from a list", and a
	 * human name is the better answer when one exists — whereas the display name
	 * is the account's formal identity. Ordering it this way also leaves every
	 * existing account's label untouched.
	 */
	companyName?: string | null;
	address?: string | null;
	id?: string;
}

export const accountDisplayLabel = (a: AccountLabelInput): string => {
	const display = a.externalName?.trim();
	if (display) return display;
	const namePart = a.primaryOwnerName?.trim() || a.companyName?.trim() || a.address?.trim() || '';
	if (a.accountNumber && namePart) return `${a.accountNumber} · ${namePart}`;
	if (a.accountNumber) return a.accountNumber;
	if (namePart) return namePart;
	return (a.id ?? '').slice(0, 8);
};

/**
 * The account "display name" (#322): the explicit `externalName` if set,
 * otherwise auto-derived as "<account #/id> - <owner last name> <flag codes>"
 * (e.g. "7587HG - Jones L"). Pure derivation — always reflects the current
 * owner/flags. Show this wherever an account is referenced by its id/number
 * (reports, deposit slips, statements, the account header).
 */
export interface AccountDisplayFields {
	externalName?: string | null;
	/** Legal name of the entity that owns the unit, held on the account itself
	 *  (#424). Outranks `primaryOwnerCompanyName`: an account-level company is a
	 *  deliberate statement about THIS unit, whereas the owner's company is
	 *  inherited from whichever person happens to be attached. */
	companyName?: string | null;
	/** "First Last" of the primary owner. */
	primaryOwnerName?: string | null;
	/** Company/legal name of the primary owner (e.g. "The RWM Trust"). Preferred
	 *  over the personal last name in the derived fallback so trusts/LLCs read
	 *  correctly. */
	primaryOwnerCompanyName?: string | null;
	accountNumber?: string | null;
	id?: string | null;
	/** Flag codes — an array (["L","DL"]) or a pre-joined string. */
	flags?: readonly string[] | string | null;
}

/**
 * Split a "First Last" string into its parts. The LAST whitespace token is the
 * surname, so "Mary Jo Van Berg" → { firstName: 'Mary Jo Van', lastName: 'Berg' }
 * and a lone "Jones" is treated as a surname (matching the "7587HG - Jones"
 * derivation below). Used by the customers list to offer sort-by-last-name
 * columns for accounts whose owner isn't in the attached-people map (#426).
 */
export const splitFullName = (name?: string | null): { firstName: string; lastName: string } => {
	const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return { firstName: '', lastName: '' };
	return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
};

const lastNameOf = (name?: string | null): string => splitFullName(name).lastName;

const flagCodes = (flags?: readonly string[] | string | null): string => {
	if (!flags) return '';
	const arr = Array.isArray(flags) ? flags : String(flags).split(/[,\s]+/);
	return arr.map((f) => String(f).trim()).filter(Boolean).join(' ');
};

export const accountDisplayName = (a: AccountDisplayFields): string => {
	const explicit = a.externalName?.trim();
	if (explicit) return explicit;
	const code = (a.accountNumber ?? '').trim() || (a.id ? String(a.id).slice(0, 8) : '');
	// Company/legal name (trust/LLC) wins over the personal last name; the
	// account's own company outranks the attached owner's.
	const owner = a.companyName?.trim() || a.primaryOwnerCompanyName?.trim() || lastNameOf(a.primaryOwnerName);
	const head = [code, owner].filter(Boolean).join(' - ');
	return [head, flagCodes(a.flags)].filter(Boolean).join(' ');
};

/**
 * The account header name: `accountDisplayName`, with the owner filled in from the attached
 * people when the single-account endpoint (which does not join the owner) left it blank.
 * Prefers the owner-role person (`pickOwnerPerson`). Shared by the account page header and
 * the transfer-lineage rows so the two can never name the same account differently.
 */
export const composeAccountHeaderName = (
	account: AccountDisplayFields,
	people: AccountOwnerPerson[] | null | undefined,
): string => {
	const owner = pickOwnerPerson(people) ?? undefined;
	return accountDisplayName({
		...account,
		primaryOwnerName: account.primaryOwnerName
			?? ([owner?.firstName, owner?.lastName].filter(Boolean).join(' ').trim() || undefined),
		primaryOwnerCompanyName: account.primaryOwnerCompanyName ?? (owner?.companyName?.trim() || undefined),
	});
};
